import React from "react";
import { Col } from "reactstrap";
import dayjs from "dayjs";
import { Tag } from "antd";
import { FolderOpenOutlined, FolderOutlined } from "@ant-design/icons";
import { Popover } from "antd";

const labelStyle = { whiteSpace: "nowrap", width: "240px", fontWeight: 600 };
const valueStyle = { flex: 1 };
const rowStyle = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
  gap: "16px",
};

const formatDate = (date: string) => {
  if (!date) return "--";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD-MMM-YYYY") : date;
};

type GeneralInfoSectionProps = {
  tenderFor: string;
  renderTenderForTag: (text: string) => React.ReactElement | null;
  generalApprovals: any[];
  tenderData?: any;
  bidDetails?: any;
};

const BlueCircle = () => (
  <span
    style={{
      display: "inline-block",
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: "2px solid #2263b3",
      background: "#fff",
      boxSizing: "border-box",
    }}
  />
);

const GeneralInfoApprovalPopover: React.FC<{ approvals: any[] }> = ({
  approvals,
}) => (
  <div>
    <div
      style={{
        color: "#2263b3",
        fontWeight: 600,
        fontSize: 18,
        marginBottom: 8,
      }}
    >
      Approval Hierarchy
    </div>
    <div style={{ position: "relative", paddingLeft: 0 }}>
      {approvals.length === 0 ? (
        <div style={{ color: "#888", fontSize: 14 }}>
          No approval data available.
        </div>
      ) : (
        approvals.map((a, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              marginBottom: i !== approvals.length - 1 ? 10 : 0,
              position: "relative",
              minHeight: 38,
            }}
          >
            <div
              style={{
                width: 20,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <BlueCircle />
              {i !== approvals.length - 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    left: "50%",
                    width: 2,
                    height: 50,
                    background: "#bdbdbd",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                />
              )}
            </div>
            <div style={{ paddingTop: 0, lineHeight: 1.3 }}>
              <div style={{ fontWeight: 600, color: "#222", fontSize: 15 }}>
                {a.userName || a.name}
              </div>
              <div style={{ fontSize: 13, color: "#888" }}>
                {a.level || a.role}
              </div>
              {a.approvalStatus && (
                <div style={{ fontSize: 13, color: "#888" }}>
                  {a.approvalStatus}
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const GeneralInfoSection: React.FC<GeneralInfoSectionProps> = ({
  tenderFor,
  renderTenderForTag,
  generalApprovals,
  tenderData,
  bidDetails,
}) => {
  const data = tenderData || {};
  return (
    <Col className="justify-start w-100 flex-wrap" style={{ display: "flex" }}>
      <div className="w-30 border-end" style={{ flex: 1, minWidth: 250 }}>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Tender
          </div>
          <div className="data-value" style={valueStyle}>
            {data.tenderNumber || data.tenderTitle || "--"}
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Type
          </div>
          <div className="data-value" style={valueStyle}>
            <Tag
              icon={
                data.tenderType === "Open" ? (
                  <FolderOpenOutlined />
                ) : (
                  <FolderOutlined />
                )
              }
              color={data.tenderType === "Open" ? "green" : "blue"}
            >
              {data.tenderType || "--"}
            </Tag>
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Tender Description
          </div>
          <div className="data-value" style={valueStyle}>
            {data.description || "--"}
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Evaluation Approvals
          </div>
          <div className="data-value" style={valueStyle}>
            <Popover
              content={
                <GeneralInfoApprovalPopover approvals={generalApprovals} />
              }
              placement="right"
              trigger={["hover"]}
              overlayStyle={{ minWidth: 220 }}
            >
              <span
                style={{
                  color: "#2263b3",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {generalApprovals.length > 0
                  ? `${generalApprovals.length} Approver(s)`
                  : "--"}
              </span>
            </Popover>
          </div>
        </div>
      </div>
      <div
        className="w-30 border-end paddingLeft10"
        style={{ flex: 1, minWidth: 250 }}
      >
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Tender For
          </div>
          <div className="data-value" style={valueStyle}>
            {renderTenderForTag(tenderFor)}
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Site / Project Name
          </div>
          <div className="data-value" style={valueStyle}>
            {data.siteName || "--"}
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Bid Status
          </div>
          <div className="data-value" style={valueStyle}>
            {bidDetails?.bidStatus ? (
              <Tag color="gold">{bidDetails.bidStatus}</Tag>
            ) : (
              "--"
            )}
          </div>
        </div>
      </div>
      <div className="w-30 paddingLeft10" style={{ flex: 1, minWidth: 250 }}>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Last Submission Date
          </div>
          <div className="data-value" style={valueStyle}>
            {formatDate(data.lastSubmissionDate)}
          </div>
        </div>
        <div className="data-col align-center pt-1" style={rowStyle}>
          <div className="data-label" style={labelStyle}>
            Last Date For Expressing Interest
          </div>
          <div className="data-value" style={valueStyle}>
            {formatDate(data.lastDateForExpressingInterest)}
          </div>
        </div>
        {bidDetails?.submittedAt && (
          <div className="data-col align-center pt-1" style={rowStyle}>
            <div className="data-label" style={labelStyle}>
              Bid Submitted At
            </div>
            <div className="data-value" style={valueStyle}>
              {dayjs(bidDetails.submittedAt).format("DD-MMM-YYYY hh:mm A")}
            </div>
          </div>
        )}
      </div>
    </Col>
  );
};

export default GeneralInfoSection;
